import { paddingHex } from '@common/utils'
import {
  ArrowBackIcon,
  ArrowForwardIcon,
  Button,
  ButtonMenu,
  ButtonMenuItem,
  CardBody,
  Flex,
  Grid,
  Skeleton,
  Text,
  useModal,
} from '@library/uikit'
import axios from 'axios'
import { FetchStatus } from 'config/constants/types'
import { useTranslation } from '@library/localization'
import { useBlindBox, useItemsContract } from 'hooks/useContract'
import { useDagenApi } from 'hooks/useDagenApi'
import clone from 'lodash/clone'
import { useSession } from 'next-auth/react'
import { useEffect, useState } from 'react'
import useSWR from 'swr'
import { RightAlignedInput } from 'views/Gens/Market/components/BuySellModals/SellModal/styles'
import { Arrow, PageButtons, PageText } from 'views/Gens/Market/YourCollections'
import { NotFoundImage } from '../../../components/ComingSoon'
import BlindBoxCard from './BlindBoxCard'
import BlindBoxResultModal from './BlindBoxResultModal'

const MAX_PER_PAGE = 12

interface BlindBox {
  boxId: string
  index: number
  amounts?: number[]
  ids?: number[]
  image: string
  mint: boolean
}

export default function PublicSellSection() {
  const { t } = useTranslation()
  const [buying, setBuying] = useState(false)
  const [blindBoxes, setBlindBoxes] = useState<BlindBox[]>([])
  const [openedIds, setOpenedIds] = useState([])
  const { data: session } = useSession()
  const [images, setImages] = useState<{ [key: number]: string }>({})
  const { reader: itemsReader } = useItemsContract()
  const placeHolders = new Array(12).fill(1)
  const [activeButtonIndex, setActiveButtonIndex] = useState(1)
  const dagenApi = useDagenApi()
  const [paginationData, setPaginationData] = useState<{
    currentPage: number
    maxPage: number
  }>({
    currentPage: 1,
    maxPage: 1,
  })

  const [inputPage, setInputPage] = useState('')

  const [onPresentBlindBoxResultModal] = useModal(
    <BlindBoxResultModal ids={openedIds} images={images} />,
    false,
    true,
    'BlindBoxResultModal',
  )

  const onSuccess = (index: number, ids: number[]) => {
    setTimeout(async () => {
      const promises = []
      for (let i = 0; i < ids.length; i++) {
        const id = ids[i]
        promises.push(axios.get(`${(await itemsReader.uri(id)).replace('{id}', paddingHex(id))}`))
      }
      Promise.all(promises)
        .then((resArray) => {
          const tmp: any = {}
          for (let i = 0; i < resArray.length; i++) {
            const res = resArray[i]
            tmp[ids[i]] = res.data.image
            // preload
            const img = new Image()
            img.src = res.data.image
          }
          setImages(tmp)
          setOpenedIds(ids)
          onPresentBlindBoxResultModal()
        })
        .catch(console.error)
    }, 2000)

    // set blind box opened
    const bb = clone(blindBoxes)
    bb[index].image = '/images/igos/opened.jpg'
    setBlindBoxes(bb)
  }

  const { data: boxes, status } = useSWR(
    [`queryBlindBox`, session ? 'currency' : 'eth', activeButtonIndex !== 0, paginationData.currentPage - 1],
    async (url, type, onSale, page) => {
      const res = await dagenApi.get(url, {
        params: { onSale, type, page, limit: MAX_PER_PAGE },
      })
      if (!res.data.success) return []

      setPaginationData({
        currentPage: page + 1,
        maxPage: Math.ceil(res.data.result.total / MAX_PER_PAGE) || 1,
      })

      return res.data.result.boxes
    },
    {
      revalidateOnFocus: false,
    },
  )

  useEffect(() => {
    if (boxes && boxes.length) {
      const tmp: BlindBox[] = boxes.map((box) => {
        return {
          boxId: box.boxId,
          index: box.index,
          image: box.mint ? '/images/igos/opened.jpg' : '/images/igos/close.jpg',
          mint: box.mint,
        }
      })
      setBlindBoxes(tmp)
    }
  }, [boxes])

  return (
    <>
      <ButtonMenu
        fullWidth={false}
        marginBottom={20}
        scale="sm"
        activeIndex={activeButtonIndex}
        onItemClick={setActiveButtonIndex}
      >
        <ButtonMenuItem minWidth={100}>{t('All')}</ButtonMenuItem>
        <ButtonMenuItem minWidth={100}>{t('On Sale')}</ButtonMenuItem>
      </ButtonMenu>
      {status !== FetchStatus.Fetched ? (
        <Grid
          gridGap="16px"
          gridTemplateColumns={['1fr', null, 'repeat(3, 1fr)', null, 'repeat(4, 1fr)']}
          alignItems="stretch"
        >
          {placeHolders.map((p, index) => (
            <CardBody key={index} p="8px">
              <Skeleton width="100%" height="300px" />
            </CardBody>
          ))}
        </Grid>
      ) : blindBoxes.length ? (
        <>
          <Grid
            gridGap="40px"
            gridTemplateColumns={['1fr', null, 'repeat(3, 1fr)', null, 'repeat(4, 1fr)']}
            alignItems="stretch"
          >
            {blindBoxes.map((blindBox, index) => {
              return (
                <BlindBoxCard
                  key={blindBox.index}
                  index={index}
                  blindBoxIndex={blindBox.index}
                  boxId={blindBox.boxId}
                  image={blindBox.image}
                  buying={buying}
                  mint={blindBox.mint}
                  setBuying={setBuying}
                  onSuccess={onSuccess}
                />
              )
            })}
          </Grid>

          <Flex pt="24px" flexDirection="column" justifyContent="space-between" height="100%">
            <PageButtons>
              <Arrow
                onClick={() => {
                  if (paginationData.currentPage !== 1) {
                    setPaginationData({
                      currentPage: paginationData.currentPage - 1,
                      maxPage: paginationData.maxPage,
                    })
                  }
                }}
              >
                <ArrowBackIcon color={paginationData.currentPage === 1 ? 'textDisabled' : 'white'} />
              </Arrow>

              <PageText>
                {t('Page %page% of %maxPage%', {
                  page: paginationData.currentPage,
                  maxPage: paginationData.maxPage,
                })}
              </PageText>
              <Arrow
                onClick={async () => {
                  if (paginationData.currentPage !== paginationData.maxPage) {
                    setPaginationData({
                      currentPage: paginationData.currentPage + 1,
                      maxPage: paginationData.maxPage,
                    })
                  }
                }}
              >
                <ArrowForwardIcon
                  color={paginationData.currentPage === paginationData.maxPage ? 'textDisabled' : 'white'}
                />
              </Arrow>

              <RightAlignedInput
                width="80px"
                scale="sm"
                type="number"
                step="1"
                min="0"
                inputMode="decimal"
                value={inputPage}
                onChange={(e) => setInputPage(e.target.value)}
              />

              <Button
                mx="8px"
                scale="sm"
                variant="round"
                onClick={() => {
                  setPaginationData({
                    currentPage: Number(inputPage),
                    maxPage: paginationData.maxPage,
                  })
                }}
              >
                Go
              </Button>
            </PageButtons>
          </Flex>
        </>
      ) : (
        <Flex alignItems="center" py="48px" flexDirection="column">
          <NotFoundImage />
          <Text fontWeight={600}>{t('No blind boxes found')}</Text>
        </Flex>
      )}
    </>
  )
}
